import { useCustomerSimple } from "@commercekit/remix";
import { isFilled } from "@prismicio/client";
import { PrismicLink } from "@prismicio/react";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { DiscountCodeBlockData } from "~/routes-server/$slug/types";

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

// Empty or invalid editor values fall back to the design defaults in custom.css.
const color = (value: string) => (HEX_COLOR.test(value.trim()) ? value.trim() : undefined);

const isB2B15Customer = (customerType?: string | null) =>
  !!customerType?.toLowerCase().startsWith("b2b15");

const WvDiscountCodeBlock = ({ block }: { block: DiscountCodeBlockData }) => {
  const { customer } = useCustomerSimple();
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const style = useMemo(() => {
    const { colors } = block;
    const vars: Record<string, string | undefined> = {
      "--dc-bg": color(colors.background),
      "--dc-text": color(colors.text),
      "--dc-button-bg": color(colors.buttonBackground),
      "--dc-button-text": color(colors.buttonText),
      "--dc-input-bg": color(colors.inputBackground),
      "--dc-input-text": color(colors.inputText),
    };
    return Object.fromEntries(
      Object.entries(vars).filter(([, value]) => value !== undefined)
    ) as CSSProperties;
  }, [block]);

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  if (isB2B15Customer(customer?.type)) return null;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(block.code);
      setCopied(true);
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard can be blocked; the code stays visible to copy by hand.
    }
  };

  const showCopied = copied && !!block.copiedText;

  return (
    <div className="pdp__order-discount-code" style={style}>
      {block.title ? <div className="mb-2 heading sm strong sans-serif">{block.title}</div> : null}
      {block.description ? <p className="mb-3">{block.description}</p> : null}
      <div className="pdp__order-discount-code__row">
        <div className="pdp__order-discount-code__code">{block.code}</div>
        {block.buttonText ? (
          <button
            type="button"
            className={classNames("btn pdp__order-discount-code__button", {
              "pdp__order-discount-code__button--copied": showCopied,
            })}
            onClick={copyCode}
            aria-live="polite"
          >
            <svg
              className="mr-2"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              {showCopied ? (
                <path d="M4 12l5 5L20 6" />
              ) : (
                <>
                  <rect x="9" y="9" width="11" height="11" />
                  <path d="M5 15V5h10" />
                </>
              )}
            </svg>
            <span className="pdp__order-discount-code__labels">
              <span style={{ visibility: showCopied ? "hidden" : "visible" }}>
                {block.buttonText}
              </span>
              <span style={{ visibility: showCopied ? "visible" : "hidden" }}>
                {block.copiedText || block.buttonText}
              </span>
            </span>
          </button>
        ) : null}
      </div>
      {block.termsText && isFilled.link(block.termsLink) ? (
        <div className="mt-2 small">
          <PrismicLink field={block.termsLink} className="pdp__order-discount-code__terms">
            {block.termsText}
          </PrismicLink>
        </div>
      ) : null}
    </div>
  );
};

export default WvDiscountCodeBlock;
